/**
 * Parse YYYY-MM-DD HH:MM in the shop's IANA timezone to a UTC timestamp.
 *
 * @param {string} dateTimeStr
 * @param {string} timeZone
 * @returns {number|undefined}
 */
function parseStoreDateTime(dateTimeStr, timeZone) {
  if (!dateTimeStr || !timeZone) return undefined;

  const match = dateTimeStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] ?? '0');

  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    Number.isNaN(second)
  ) {
    return undefined;
  }

  const targetMs = Date.UTC(year, month - 1, day, hour, minute, second);
  let utcGuess = targetMs;

  const getZonedParts = (ms) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date(ms));

    /** @type {Record<string, number>} */
    const values = {};

    for (const part of parts) {
      if (part.type === 'literal') continue;
      values[part.type] = Number(part.value);
    }

    if (values.hour === 24) values.hour = 0;

    return values;
  };

  for (let attempt = 0; attempt < 5; attempt++) {
    const zoned = getZonedParts(utcGuess);
    const actualMs = Date.UTC(
      zoned.year,
      zoned.month - 1,
      zoned.day,
      zoned.hour,
      zoned.minute,
      zoned.second
    );
    const diff = targetMs - actualMs;

    if (diff === 0) return utcGuess;

    utcGuess += diff;
  }

  return utcGuess;
}

/**
 * @param {DOMStringMap} dataset
 * @param {'end' | 'start'} type
 * @returns {number|undefined}
 */
function resolveTimestamp(dataset, type) {
  const datetimeKey = type === 'end' ? 'endDatetime' : 'startDatetime';
  const dateTime = dataset[datetimeKey];
  const timeZone = dataset.timezone;

  if (!dateTime || !timeZone) return undefined;

  return parseStoreDateTime(dateTime, timeZone);
}

/**
 * Countdown timer for announcement bar slides.
 */
class AnnouncementCountdownComponent extends HTMLElement {
  /** @type {number|undefined} */
  #interval;

  /** @type {number|undefined} */
  #endTime;

  /** @type {number|undefined} */
  #startTime;

  connectedCallback() {
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();

    const endTime = resolveTimestamp(this.dataset, 'end');
    if (!endTime || Number.isNaN(endTime)) return;

    this.#endTime = endTime;
    this.#startTime = resolveTimestamp(this.dataset, 'start');

    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  stop() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  #tick() {
    if (this.#endTime === undefined) return;

    const now = Date.now();

    if (this.#startTime !== undefined && now < this.#startTime) {
      this.#handleBeforeStart(this.#startTime - now);
      return;
    }

    this.#showSlide();

    const remaining = this.#endTime - now;
    const expiredBehavior = this.dataset.expiredBehavior ?? 'show_zeros';

    if (remaining <= 0) {
      this.#handleExpired(expiredBehavior);
      return;
    }

    this.#showCountdown();
    this.#updateUnits(remaining);
  }

  /**
   * @param {number} remaining
   */
  #handleBeforeStart(remaining) {
    const behavior = this.dataset.beforeStartBehavior ?? 'hide_countdown';

    if (behavior === 'hide_announcement') {
      this.#hideSlide();
      return;
    }

    this.#showSlide();

    if (behavior === 'countdown_to_start') {
      this.#showCountdown();
      this.#updateUnits(remaining);
      return;
    }

    const display = this.querySelector('.announcement-bar__countdown-display');
    const expiredMessage = this.querySelector('.announcement-bar__countdown-expired');

    if (display instanceof HTMLElement) display.hidden = true;
    if (expiredMessage instanceof HTMLElement) expiredMessage.hidden = true;
  }

  /**
   * @param {number} remaining
   */
  #updateUnits(remaining) {
    const showDays = this.dataset.showDays === 'true';
    const showHours = this.dataset.showHours === 'true';
    const showMinutes = this.dataset.showMinutes === 'true';
    const showSeconds = this.dataset.showSeconds === 'true';

    let diff = remaining;

    const days = showDays ? Math.floor(diff / 86400000) : 0;
    if (showDays) diff -= days * 86400000;

    const hours = showHours ? Math.floor(diff / 3600000) : 0;
    if (showHours) diff -= hours * 3600000;

    const minutes = showMinutes ? Math.floor(diff / 60000) : 0;
    if (showMinutes) diff -= minutes * 60000;

    const seconds = showSeconds ? Math.floor(diff / 1000) : 0;

    this.#setUnitValue('days', days);
    this.#setUnitValue('hours', hours);
    this.#setUnitValue('minutes', minutes);
    this.#setUnitValue('seconds', seconds);
    this.#updateSeparators();
  }

  /**
   * @param {string} unit
   * @param {number} value
   */
  #setUnitValue(unit, value) {
    const element = this.querySelector(`[data-countdown-${unit}]`);
    if (element) element.textContent = String(value).padStart(2, '0');
  }

  #updateSeparators() {
    const units = this.querySelectorAll('[data-countdown-unit]');
    const separators = this.querySelectorAll('.announcement-bar__countdown-separator');

    units.forEach((unit, index) => {
      const separator = separators[index];
      if (!(separator instanceof HTMLElement)) return;

      const nextUnit = units[index + 1];
      separator.hidden = !nextUnit || unit.hidden || nextUnit.hidden;
    });
  }

  /**
   * @param {string} behavior
   */
  #handleExpired(behavior) {
    this.stop();

    if (behavior === 'hide') {
      this.#hideSlide();
      return;
    }

    const display = this.querySelector('.announcement-bar__countdown-display');
    const expiredMessage = this.querySelector('.announcement-bar__countdown-expired');

    if (behavior === 'show_message') {
      if (display instanceof HTMLElement) display.hidden = true;
      if (expiredMessage instanceof HTMLElement) expiredMessage.hidden = false;
      return;
    }

    if (display instanceof HTMLElement) display.hidden = false;
    if (expiredMessage instanceof HTMLElement) expiredMessage.hidden = true;
    this.#updateUnits(0);
  }

  #showCountdown() {
    const display = this.querySelector('.announcement-bar__countdown-display');
    const expiredMessage = this.querySelector('.announcement-bar__countdown-expired');

    if (display instanceof HTMLElement) display.hidden = false;
    if (expiredMessage instanceof HTMLElement) expiredMessage.hidden = true;
  }

  #hideSlide() {
    const slide = this.closest('.announcement-bar__slide');
    if (slide instanceof HTMLElement) slide.hidden = true;
    this.#refreshCarousel();
  }

  #showSlide() {
    const slide = this.closest('.announcement-bar__slide');
    if (slide instanceof HTMLElement) slide.hidden = false;
    this.#refreshCarousel();
  }

  #refreshCarousel() {
    const carousel = this.closest('announcement-bar-component');
    if (carousel) carousel.current = carousel.current;
  }
}

if (!customElements.get('announcement-countdown-component')) {
  customElements.define('announcement-countdown-component', AnnouncementCountdownComponent);
}

document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('announcement-countdown-component').forEach((element) => {
    if (element instanceof AnnouncementCountdownComponent) {
      element.start();
    }
  });
});

document.addEventListener('shopify:block:select', () => {
  requestAnimationFrame(() => {
    document.querySelectorAll('announcement-countdown-component').forEach((element) => {
      if (element instanceof AnnouncementCountdownComponent) {
        element.start();
      }
    });
  });
});
